import { useState, useEffect } from "react";
import { useJsApiLoader } from "@react-google-maps/api";

interface LocationState {
  lat: number | null;
  lng: number | null;
  place: string;
  error: string | null;
}

const useFetchCurrentLocation = () => {
  const [location, setLocation] = useState<LocationState>({
    lat: null,
    lng: null,
    place: "",
    error: null,
  });

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "", // Leave this blank, API key is in public/index.html
  });

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocation((prev) => ({
        ...prev,
        error: "Geolocation is not supported by your browser",
      }));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setLocation((prev) => ({
          ...prev,
          lat: latitude,
          lng: longitude,
          error: null,
        }));

        if (isLoaded) {
          try {
            const geocoder = new window.google.maps.Geocoder();
            const result = await geocodeLatLng(geocoder, latitude, longitude);
            setLocation((prev) => ({
              ...prev,
              place: result,
            }));
          } catch (err) {
            setLocation((prev) => ({
              ...prev,
              error: "Error fetching place information",
            }));
          }
        }
      },
      () => {
        setLocation((prev) => ({
          ...prev,
          error: "Unable to retrieve location",
        }));
      }
    );
  }, [isLoaded]);

  const geocodeLatLng = (
    geocoder: google.maps.Geocoder,
    lat: number,
    lng: number
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const latlng = { lat, lng };
      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === "OK") {
          if (results && results[0]) {
            resolve(results[0].formatted_address);
          } else {
            reject("No results found");
          }
        } else {
          reject(`Geocoder failed due to: ${status}`);
        }
      });
    });
  };

  return location;
};

export default useFetchCurrentLocation;
